import { computed, reactive } from "vue";
import { isGepPwaInstallPromptEnabled } from "./gepPwa";

export type InstallPlatform = "android" | "ios" | "other";
export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

/** Chrome's non-standard install event; not in lib.dom.d.ts. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Set only when the guest closes the banner or finishes installing. Nothing
// else hides it: not time, not dismissing Chrome's install dialog, not reading
// the shortcut guide.
const CLOSED_KEY = "gepInstallBannerClosed";

const state = reactive({
  deferred: null as BeforeInstallPromptEvent | null,
  installed: false,
  closed: false,
  // One impression per page load, however often Home is re-mounted.
  shownReported: false,
});

let initialised = false;

function readClosed(): boolean {
  try {
    return localStorage.getItem(CLOSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeClosed(): void {
  state.closed = true;
  try {
    localStorage.setItem(CLOSED_KEY, "1");
  } catch {
    // Private mode / blocked storage: it stays closed for this visit only.
  }
}

/** iPadOS 13+ Safari reports a desktop Mac user agent, so check touch too. */
export function detectPlatform(
  ua: string = navigator.userAgent,
  maxTouchPoints: number = navigator.maxTouchPoints || 0,
): InstallPlatform {
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/macintosh/i.test(ua) && maxTouchPoints > 1) return "ios";
  if (/android/i.test(ua)) return "android";
  return "other";
}

/**
 * The Android guide describes Chrome's own menu ("Add to Home screen" ->
 * "Create shortcut"); Samsung Internet, Edge, Opera and Firefox word it
 * differently, so they only get the real Install button when the browser
 * offers one.
 */
export function isAndroidChrome(ua: string = navigator.userAgent): boolean {
  return (
    /android/i.test(ua) &&
    /Chrome\//.test(ua) &&
    !/SamsungBrowser|EdgA|OPR\/|OPT\/|Vivaldi|DuckDuckGo|UCBrowser|YaBrowser|MiuiBrowser|HuaweiBrowser/i.test(
      ua,
    )
  );
}

/**
 * In-app browsers (social / chat apps) cannot add a Home Screen shortcut.
 * iOS WKWebViews omit the "Safari/" token that Safari, Chrome and Firefox keep.
 */
export function isInAppBrowser(
  ua: string = navigator.userAgent,
  platform: InstallPlatform = detectPlatform(ua),
): boolean {
  if (
    /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|WeChat|Zalo|KAKAOTALK|Snapchat|TikTok|musical_ly|Twitter|GSA\/|; wv\)/i.test(
      ua,
    )
  )
    return true;
  return platform === "ios" && !/safari/i.test(ua);
}

export function isStandalone(): boolean {
  try {
    if (
      window.matchMedia?.(
        "(display-mode: standalone), (display-mode: minimal-ui), (display-mode: fullscreen)",
      ).matches
    )
      return true;
  } catch {
    // matchMedia unavailable: fall through to the iOS flag.
  }
  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Attach the install listeners at boot: Chrome fires `beforeinstallprompt`
 * long before Home mounts. Only the pilot page is touched, so other hotels keep
 * Chrome's own install UI.
 */
export function initInstallPrompt(): void {
  if (initialised || typeof window === "undefined") return;
  if (!isGepPwaInstallPromptEnabled()) return;
  initialised = true;
  state.closed = readClosed();
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    state.deferred = event as BeforeInstallPromptEvent;
  });
  window.addEventListener("appinstalled", () => {
    state.deferred = null;
    state.installed = true;
    writeClosed();
  });
}

export function useInstallPrompt() {
  const platform = detectPlatform();
  const inAppBrowser = isInAppBrowser(navigator.userAgent, platform);
  const standalone = isStandalone();
  const enabled = isGepPwaInstallPromptEnabled();

  // iPhone and Chrome on Android always get a how-to guide; every other
  // browser only when it has actually offered the install event.
  const guideAvailable =
    platform === "ios" ||
    (platform === "android" && isAndroidChrome(navigator.userAgent));

  const canPrompt = computed(() => state.deferred !== null);
  const installed = computed(() => state.installed || standalone);
  const visible = computed(
    () =>
      enabled &&
      !installed.value &&
      !inAppBrowser &&
      !state.closed &&
      (canPrompt.value || guideAvailable),
  );

  /** True once per page load, so the "shown" metric is not inflated by remounts. */
  function claimShownReport(): boolean {
    if (state.shownReported) return false;
    state.shownReported = true;
    return true;
  }

  /** The guest closed the banner: it stays closed on this phone. */
  function closeBanner(): void {
    writeClosed();
  }

  /** Must run from a user gesture; the browser allows one prompt per event. */
  async function promptInstall(): Promise<InstallOutcome> {
    const event = state.deferred;
    if (!event) return "unavailable";
    state.deferred = null;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (choice.outcome === "accepted") {
        closeBanner();
        return "accepted";
      }
      // Cancelling Chrome's dialog is not closing the banner; it stays.
      return "dismissed";
    } catch {
      return "unavailable";
    }
  }

  return {
    platform,
    inAppBrowser,
    standalone,
    guideAvailable,
    canPrompt,
    installed,
    visible,
    claimShownReport,
    closeBanner,
    promptInstall,
  };
}
